import { Check, Copy } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { cn } from '@/shared/lib/utils';

/**
 * Copy a value to the clipboard.
 *
 * Operators copy order ids and transaction references constantly when talking
 * to vendors or support, and selecting monospace text by hand is error-prone.
 *
 * The confirmation is inline (the icon swaps for two seconds) rather than a
 * toast, because copying is high-frequency and a toast per copy would be noise.
 */
export function CopyButton({
  value,
  label = 'value',
  className,
}: {
  value: string;
  /** Used in the accessible name: "Copy transaction reference". */
  label?: string;
  className?: string;
}) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async (button: HTMLButtonElement) => {
    if (await copyText(value, button)) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } else {
      // Fail loudly rather than leaving the user thinking the copy worked.
      toast.error('Could not copy to the clipboard.');
    }
  };

  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={(event) => void handleCopy(event.currentTarget)}
      className={cn('size-6 shrink-0', className)}
      aria-label={copied ? `Copied ${label}` : `Copy ${label}`}
    >
      {copied ? (
        <Check className="text-status-success size-3" aria-hidden />
      ) : (
        <Copy className="size-3" aria-hidden />
      )}
    </Button>
  );
}

/**
 * `navigator.clipboard` exists only in secure contexts, and the admin is
 * deployed over plain HTTP on an internal host (cash-app-dev.we.local), where
 * it is `undefined`. There the legacy `execCommand('copy')` still works.
 */
async function copyText(value: string, button: HTMLButtonElement): Promise<boolean> {
  if (window.isSecureContext && navigator.clipboard) {
    try {
      await navigator.clipboard.writeText(value);
      return true;
    } catch {
      // Permission denied — try the legacy path below.
    }
  }

  // The textarea goes next to the button, not on <body>: inside a modal
  // dialog, Radix's focus trap pulls focus back from anything outside it, and
  // execCommand copies only the focused selection.
  const textarea = document.createElement('textarea');
  textarea.value = value;
  textarea.readOnly = true;
  textarea.setAttribute('aria-hidden', 'true');
  textarea.style.position = 'fixed';
  textarea.style.opacity = '0';
  textarea.style.pointerEvents = 'none';
  (button.parentElement ?? document.body).appendChild(textarea);
  try {
    textarea.focus();
    textarea.select();
    return document.execCommand('copy');
  } catch {
    return false;
  } finally {
    textarea.remove();
    button.focus();
  }
}
